import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { getBrandingForHost } from '~/api';
import { BrandingConfig, TenantBranding } from '~/api/types';
import config from '~/config';

function getHost() {
  return window.location.host.toLowerCase();
}

function isCubHost() {
  return getHost() === config.CUB_HOST.toLowerCase();
}

/**
 * The branding of the tenant served at the host, in both flows: the identity
 * validation links are also served at the tenant's own host. The CUB host
 * always renders with the CUB defaults, so its branding is never asked for.
 *
 * Shared so that the header can read the branding without it being threaded
 * through every wizard step: all callers hit the same react-query cache entry,
 * so the request is still made once.
 */
export function useBrandingQuery() {
  const host = getHost();

  return useQuery<TenantBranding | null, AxiosError, TenantBranding | null, string[]>(
    ['branding', host],
    () => getBrandingForHost(host),
    { enabled: !isCubHost(), retry: false },
  );
}

/**
 * Who the header and footer are branded for. A host other than CUB's belongs to
 * a whitelabel tenant even without branding configured: its logo is served from
 * the tenant's folder of the whitelabel assets bucket.
 */
export function useTenant(): Tenant {
  const { data } = useBrandingQuery();

  const host = getHost();
  const label = host.split('.')[0].replace(/-cliente$/g, '');
  const hostLogoUrl = isCubHost() ? undefined : `${config.WHITELABEL_ASSETS_BASE_URL}/${label}/header-logo.png`;

  if (data) {
    return {
      tenant: 'whitelabel',
      companyName: data.companyName,
      companyLogoUrl: data.branding.companyLogoUrl || hostLogoUrl,
      branding: data.branding,
    };
  }

  if (hostLogoUrl) return { tenant: 'whitelabel', companyLogoUrl: hostLogoUrl };

  return { tenant: 'cub' };
}

interface Tenant {
  tenant: 'cub' | 'whitelabel';
  companyName?: string;
  companyLogoUrl?: string;
  branding?: BrandingConfig;
}
